import {
  AUDIENCE_AGE_RANGE_OPTIONS,
  AUDIENCE_GENDER_OPTIONS,
  AUDIENCE_REGION_OPTIONS,
  AUDIENCE_SOCIAL_CLASS_OPTIONS,
  AudienceGender,
  AudienceRegion,
  AudienceSocialClass,
  INTEREST_CATEGORIES
} from '../constants/audienceTargeting'

/**
 * O que este módulo precisa: dar uma chave montada e receber texto.
 */
export type Translate = (key: string) => string

/**
 * Fronteira entre o mundo de chaves tipadas e este módulo.
 *
 * O `t` do i18next **não** é atribuível a `Translate`: ele tipa a chave como
 * união literal de todas as chaves dos locales, e parâmetro de função é
 * contravariante, então uma função que só aceita chaves conhecidas não serve
 * onde se aceita qualquer string. Declarar o tipo estreito sem adaptar o `t`
 * não resolvia nada — só empurrava o erro para cada chamador, incluindo o
 * `ReviewStep`, que é criação de campanha em produção.
 *
 * A conversão fica neste ponto único, que é a mesma dívida que o comentário do
 * `VALUE_KEY_PREFIX` abaixo já localiza aqui. Em troca, um mock de teste
 * continua sendo apenas `(key) => key`.
 */
export const asTranslate = (t: typeof import('i18next').t): Translate =>
  t as unknown as Translate

/**
 * Chaves em `campaign.create.*` mesmo sendo consumidas fora da criação. A cópia
 * é a mesma — "Região geográfica", "Classe social" — e renomear o namespace
 * custaria três locales mais os testes do formulário para não mudar uma linha
 * de texto. Este módulo é o único lugar que conhece as chaves, então a dívida
 * fica endereçável de um ponto só.
 */
const VALUE_KEY_PREFIX = 'rm:campaign.create.AudienceTargeting'

export const EMPTY_VALUE = '—'

/**
 * Forma neutra de fonte. O formulário registra em camelCase e a resposta de
 * `GET /campaign/:id` devolve `audience_targeting` em snake_case; cada chamador
 * traduz a sua para cá, e a apresentação não precisa saber de qual veio.
 */
export type AudienceTargetingSnapshot = {
  region?: AudienceRegion[]
  ageRange?: string[]
  socialClass?: AudienceSocialClass[]
  gender?: AudienceGender[]
  interests?: string[]
}

/**
 * Mostra o rótulo de "todos" quando a seleção cobre a lista inteira, como o
 * protótipo faz (`syncDv360ChipGroupReview`), em vez de enumerar os valores.
 *
 * "Selecionar todos" não é um valor guardado: o campo sempre carrega a lista
 * real, cheia ou parcial, então comparar com o total conhecido é o único jeito
 * de recuperar esse estado na exibição.
 */
export const summarizeMultiSelect = <T extends string>(
  values: T[] | undefined,
  totalOptions: number,
  allLabel: string,
  translateValue?: (value: T) => string
) => {
  if (!values?.length) return EMPTY_VALUE
  if (totalOptions > 0 && values.length === totalOptions) return allLabel

  return values.map(value => translateValue?.(value) ?? value).join(', ')
}

/**
 * Os quatro campos demográficos já resumidos e traduzidos. Faixa de idade não
 * traduz valor: `35-44` é o próprio rótulo.
 */
export const buildAudienceTargetingLabels = (
  targeting: AudienceTargetingSnapshot | undefined,
  t: Translate
) => {
  const allLabel = t(`${VALUE_KEY_PREFIX}.all`)
  const allGenderLabel = t(`${VALUE_KEY_PREFIX}.allGender`)

  return {
    region: summarizeMultiSelect(
      targeting?.region,
      AUDIENCE_REGION_OPTIONS.length,
      allLabel,
      value => t(`${VALUE_KEY_PREFIX}.region.${value}`)
    ),
    ageRange: summarizeMultiSelect(
      targeting?.ageRange,
      AUDIENCE_AGE_RANGE_OPTIONS.length,
      allLabel
    ),
    socialClass: summarizeMultiSelect(
      targeting?.socialClass,
      AUDIENCE_SOCIAL_CLASS_OPTIONS.length,
      allLabel,
      value => t(`${VALUE_KEY_PREFIX}.socialClass.${value}`)
    ),
    gender: summarizeMultiSelect(
      targeting?.gender,
      AUDIENCE_GENDER_OPTIONS.length,
      allGenderLabel,
      value => t(`${VALUE_KEY_PREFIX}.gender.${value}`)
    )
  }
}

const CATEGORY_BY_SUBGROUP = new Map(
  INTEREST_CATEGORIES.flatMap(category =>
    category.subgroups.map(subgroup => [subgroup.id, category.id])
  )
)

/**
 * O rótulo do interesse é aninhado na categoria dele, então traduzir um id
 * solto exige achar a categoria primeiro.
 *
 * Id sem categoria conhecida cai no próprio id, e **não** é omitido: ao
 * contrário do varejista de destino, cujo id é UUID, aqui o id é o slug do
 * segmento na Publya (`donos_de_gatos`) e diz algo a quem lê. Esconder um
 * interesse que a campanha realmente segmenta seria pior para quem aprova do
 * que mostrar o slug.
 */
export const buildInterestLabels = (
  interests: string[] | undefined,
  t: Translate
) =>
  (interests ?? []).map(id => {
    const categoryId = CATEGORY_BY_SUBGROUP.get(id)

    if (!categoryId) return id

    return t(
      `${VALUE_KEY_PREFIX}.interests.category.${categoryId}.subgroup.${id}`
    )
  })
